import React, { useState } from 'react';
import { trackPoll } from '../utils/gtag';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Award, Users, Heart, BookOpen, ArrowRight, Shield, Mail, Star, Calendar, Globe, Phone } from 'lucide-react';
import CTA from '../components/CTA';

const About: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Integrate with Supabase or email service
    console.log('Newsletter signup:', email);
    setIsSubscribed(true);
    setEmail('');
    // Reset after 3 seconds
    setTimeout(() => setIsSubscribed(false), 3000);
  };

  const trustBadges = [
    { name: "CNHC Registered", icon: <Shield className="w-6 h-6" /> },
    { name: "AfSFH Member", icon: <Award className="w-6 h-6" /> },
    { name: "Fully Insured", icon: <Shield className="w-6 h-6" /> },
    { name: "GDPR Compliant", icon: <Shield className="w-6 h-6" /> }
  ];

  return (
    <>
      <Helmet>
        <title>About Sharon Clare | Clinical Hypnotherapist Belfast, Lisburn & Northern Ireland</title>
        <meta
          name="description"
          content="Meet Sharon Clare, CNHC registered clinical hypnotherapist in Belfast & Lisburn. Advanced Diploma (DSFH, HPD), 2+ years experience helping clients overcome anxiety, trauma, and persistent pain. In-person & online sessions."
        />
        <meta name="keywords" content="Sharon Clare, hypnotherapist Belfast, hypnotherapist Lisburn, hypnotherapist Coleraine, clinical hypnotherapist Northern Ireland, CNHC registered, AfSFH member, hypnotherapy qualifications" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/about/" />
        <meta property="og:title" content="About Sharon Clare | Clinical Hypnotherapist Belfast, Lisburn & Northern Ireland" />
        <meta property="og:description" content="Meet Sharon Clare, CNHC registered clinical hypnotherapist in Belfast & Lisburn, with 2+ years experience." />
        <meta property="og:url" content="https://www.sharonclare-hypnotherapy.com/about/" />
        <meta property="og:image" content="https://www.sharonclare-hypnotherapy.com/images/Sharon Clare.png" />
        <meta property="og:type" content="profile" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Sharon Clare | Clinical Hypnotherapist Belfast, Lisburn & Northern Ireland" />
        <meta name="twitter:description" content="Meet Sharon Clare, CNHC registered clinical hypnotherapist in Belfast & Lisburn, with 2+ years experience." />
        <meta name="twitter:image" content="https://www.sharonclare-hypnotherapy.com/images/Sharon Clare.png" />
      </Helmet>
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Hero Section with Photo */}
      <section className="py-16 sm:py-20">
        <div className="text-center mb-12">
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-200 to-accent-200 rounded-full blur-3xl opacity-30 transform scale-110"></div>
            <div className="relative bg-white rounded-full p-4 shadow-2xl">
              <img 
                src="/images/Sharon Clare.png" 
                loading="lazy"
                alt="Sharon Clare - CNHC Registered Clinical Hypnotherapist specializing in anxiety, stress, and medical procedure anxiety in Northern Ireland"
                className="w-48 h-48 sm:w-56 sm:h-56 rounded-full object-cover"
              />
            </div>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-4">
            Sharon Clare
          </h1>
          <div className="text-lg sm:text-xl text-accent-600 font-medium mb-8 space-y-1">
            <p>DSFH, HPD, CNHC, AfSFH</p>
            <p>Healthcare & NHS Experience</p>
            <p>Brainz Magazine Contributor</p>
          </div>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            {trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md border border-gray-100">
                <div className="text-accent-600">{badge.icon}</div>
                <span className="text-sm font-medium text-gray-700">{badge.name}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center items-center gap-10 mb-10">
            <img src="/images/CNHC.png" alt="CNHC Registered" loading="lazy" className="w-40 md:w-56 lg:w-64 h-auto object-contain" />
            <img src="/images/brainz-badge.png" alt="Brainz Magazine Contributor" loading="lazy" className="w-40 md:w-56 lg:w-64 h-auto object-contain" />
          </div>
          
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
            Your trusted partner in healing and transformation. With extensive healthcare and NHS experience, 
            I specialize in solution-focused hypnotherapy to help people overcome anxiety linked to physical health, 
            surgery fears, and radiotherapy anxiety. I'm passionate about supporting those facing medical procedures 
            and health-related challenges.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/book"
              className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
            >
              <Calendar className="w-5 h-5 mr-2" />
                Request a 10-Minute Call
            </a>
            <a
              href="tel:07932139245"
              className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50 fade-in">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-6">
                  Experience
                </h2>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <p>
                    I'm Sharon, a Clinical Hypnotherapist, and I help people whose lives have quietly shrunk because of fear, stress, or anxiety.
                  </p>
                  <p>
                    Before retraining as a hypnotherapist, I spent over 30 years working in health and social care, including leading a large social care organisation through the Covid pandemic. That period was pivotal for me. Professionally, it deepened my understanding of how people cope under pressure. Personally, it showed me just how profoundly prolonged stress affects the nervous system — teaching the body to stay on high alert, to brace, react, and remain on guard long after the danger has passed.
                  </p>
                  <p>
                    Like many people, I reached a point where I realised that staying in a constant state of responsibility and urgency came at a cost. Stepping away from that role, relocating to Northern Ireland to be closer to family, and doing my own nervous-system work changed everything. I now feel calm, confident, and in control. I respond rather than react — and I'm deeply committed to protecting that sense of balance.
                  </p>
                  <p>
                    That experience shapes the way I work.
                  </p>
                  <p>
                    I support people whose stress or anxiety is beginning to affect their sleep, confidence, or health. Sometimes this shows up in behaviour — avoiding certain situations, overthinking, emotional eating, or feeling stuck. Sometimes it shows up in the body — jaw clenching, teeth grinding, tension, or other stress-conditioned physical responses. Often, these patterns began as a way of coping, but are no longer serving the person.
                  </p>
                  <p>
                    A significant part of my work also supports people going through cancer treatment, including radiotherapy, who may be dealing with fear, anxiety, and a sense of lost control alongside the physical demands of treatment. My approach is calm, respectful, and practical, helping people feel steadier and more resourced during an incredibly challenging time.
                  </p>
                  <p>
                    My work is neuroscience-based and solution-focused. Rather than analysing the past in detail, we focus on helping the nervous system settle, building a sense of safety in the body, and gently restoring choice, confidence, and control.
                  </p>
                  <p>
                    I work with adults and young people (12+) from my clinics in Belfast and Lisburn, as well as online — helping people feel safe in their bodies again, and feel like you again.
                  </p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-200 to-accent-200 rounded-2xl blur-2xl opacity-30 transform scale-105"></div>
                <div className="relative bg-white rounded-2xl p-6 shadow-xl">
                  <img 
                    src="/images/sharon-with-mic.jpg" 
                    loading="lazy"
                    alt="Sharon Clare presenting at healthcare conference - Clinical Hypnotherapist and Brainz Magazine contributor"
                    className="w-full h-auto rounded-xl object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials & Training Section */}
      <section className="py-16 sm:py-20 bg-white fade-in">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-6">
              Credentials & Training
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Professional qualifications and ongoing training ensure you receive the highest standard of care.
            </p>
          </div>

          {/* Key Credentials Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6 text-center border border-accent-100">
              <Award className="w-8 h-8 text-accent-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">DSFH</h3>
              <p className="text-sm text-gray-600">Diploma in Solution Focused Hypnotherapy</p>
            </div>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6 text-center border border-accent-100">
              <Award className="w-8 h-8 text-accent-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">HPD</h3>
              <p className="text-sm text-gray-600">Hypnotherapy Practitioner Diploma</p>
            </div>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6 text-center border border-accent-100">
              <Shield className="w-8 h-8 text-accent-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">CNHC</h3>
              <p className="text-sm text-gray-600">Complementary & Natural Healthcare Council</p>
            </div>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6 text-center border border-accent-100">
              <Users className="w-8 h-8 text-accent-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">AfSFH</h3>
              <p className="text-sm text-gray-600">Association for Solution Focused Hypnotherapy</p>
            </div>
          </div>

          {/* Training & Approach Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-6">
                  Professional Approach
                </h3>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    I believe in treating every client with empathy, understanding, and respect for their unique journey. 
                    All treatments are grounded in proven therapeutic techniques and the latest research in hypnotherapy, 
                    using solution-focused methods that harness the power of neuroplasticity.
                  </p>
                  <p>
                    Your goals, needs, and comfort are at the center of every session. I adapt my approach to what works 
                    best for you, combining gentle hypnosis with evidence-based techniques to help you overcome anxiety 
                    linked to physical health, manage surgery fears, and cope with radiotherapy anxiety.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-6 h-6 text-accent-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Continuous Learning</h4>
                      <p className="text-gray-600 text-sm">
                        I regularly update my skills and knowledge to provide you with the most effective and current treatments, 
                        ensuring you receive the highest standard of care in a safe, supportive environment.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Heart className="w-6 h-6 text-accent-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Specialized Focus</h4>
                      <p className="text-gray-600 text-sm">
                        Special expertise in supporting those facing medical procedures and health challenges, 
                        with deep understanding of radiotherapy anxiety and surgery fears.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Interests Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50 fade-in">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-6">
              Special Interests
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Areas of expertise and passion that drive my practice and help me serve you better.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-accent-600" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                Medical Procedure Anxiety
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Specialized support for surgery fears, healthcare appointment anxiety, and medical procedure phobias. 
                Helping you feel calm and confident during treatment.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-accent-600" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                Radiotherapy Support
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive support for cancer treatment anxiety, including mask claustrophobia, 
                treatment session fears, and emotional resilience during radiotherapy.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center md:col-span-2 lg:col-span-1">
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-accent-600" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                Healthcare & NHS Experience
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Deep understanding of healthcare systems and the unique challenges faced by patients 
                and families during medical treatment and recovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Beyond Therapy Section */}
      <section className="py-16 sm:py-20 bg-white fade-in">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-6">
              Beyond the Therapy Room
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              My commitment to healing extends beyond individual sessions through education, advocacy, and community support.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-6 h-6 text-accent-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                        Education & Advocacy
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        I'm passionate about educating others about the power of hypnotherapy and mental health awareness. 
                        I regularly contribute to Brainz Magazine and speak at wellness events to help destigmatize therapy 
                        and promote holistic healing approaches, particularly for those facing health-related challenges.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-accent-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                        Continuous Learning
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        I believe in the importance of continuous learning and regularly attend professional development workshops 
                        and conferences. This commitment to growth ensures that my clients receive the most current and effective 
                        treatments available in the field of hypnotherapy, with special focus on medical procedure anxiety.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-accent-600" />
                  </div>
                  <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                    Community Support
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-center">
                  Based in Northern Ireland, I'm also an active volunteer at a local cancer center, 
                  supporting patients and families facing cancer treatment. This experience has deepened my understanding 
                  of radiotherapy anxiety and the unique challenges of medical procedures.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50 fade-in">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-6">
              Trust & Professional Standards
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Committed to the highest standards of professional practice and client care.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-gray-100">
              <Shield className="w-12 h-12 text-accent-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
              <p className="text-sm text-gray-600">Complementary & Natural Healthcare Council</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-gray-100">
              <Award className="w-12 h-12 text-accent-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">AfSFH Member</h3>
              <p className="text-sm text-gray-600">Association for Solution Focused Hypnotherapy</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-gray-100">
              <Shield className="w-12 h-12 text-accent-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Fully Insured</h3>
              <p className="text-sm text-gray-600">Professional indemnity insurance</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-gray-100">
              <Shield className="w-12 h-12 text-accent-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">GDPR Compliant</h3>
              <p className="text-sm text-gray-600">Data protection & privacy standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* Radiotherapy Support Program Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-6">
            Radiotherapy Anxiety Support Program
          </h2>
          <div className="max-w-3xl mx-auto text-lg text-gray-600 leading-relaxed space-y-6">
            <p>
              I offer specialized support for people facing radiotherapy, particularly those undergoing treatment for head and neck cancer. 
              My 3-session program is designed to be completed between treatment planning and the start of radiotherapy, 
              helping you feel prepared, calm, and confident.
            </p>
            <p>
              Radiotherapy can be overwhelming—the mask, the treatment environment, loss of appetite, and the anxiety that builds before each session. 
              My approach helps you manage these challenges so treatment feels more manageable and you can focus on healing.
            </p>
            
            {/* Interactive Poll/Question Box */}
            <div className="bg-gradient-to-r from-primary-50 to-accent-50 p-8 rounded-2xl border border-primary-100 mt-8">
              <div className="flex items-center justify-center mb-4">
                <Heart className="w-8 h-8 text-accent-600 mr-3" />
                <h3 className="text-2xl font-heading font-bold text-gray-900">
                  Would This Have Helped You?
                </h3>
              </div>
              <p className="text-gray-700 mb-6">
                If you or someone you know has faced radiotherapy anxiety, I'd love to hear from you. 
                Would a short hypnotherapy program before treatment have been helpful?
              </p>
              
              <div className="space-y-4 mb-6">
                {[
                  'Yes, this would have been incredibly helpful before my treatment',
                  "I'm currently preparing for radiotherapy and interested in this support",
                  'I know someone who could benefit from this'
                ].map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={async () => {
                      try {
                        const response = await fetch('/api/poll', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ choice: label, page: 'about-radiotherapy-poll' })
                        });
                        const result = await response.json();
                        if (!response.ok) {
                          console.error('Poll submission failed:', response.statusText, result);
                        } else {
                          trackPoll('about-radiotherapy-poll', label);
                          console.log('Poll submitted successfully:', result);
                          if (!result.saved) {
                            console.warn('Poll may not have been saved to database');
                          }
                          if (!result.emailSent) {
                            console.warn('Poll notification email may not have been sent');
                          }
                        }
                        window.location.href = '/thank-you?from=about-poll';
                      } catch (e) {
                        console.error('Error submitting poll:', e);
                        // Still redirect to thank you page for better UX
                        window.location.href = '/thank-you?from=about-poll';
                      }
                    }}
                    className="w-full text-left bg-white rounded-lg p-4 border-2 border-gray-200 hover:border-accent-400 transition-colors"
                  >
                    <p className="text-gray-900 font-medium flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" aria-hidden="true" />{label}</p>
                  </button>
                ))}
              </div>
              
              <p className="text-gray-600 mb-4 font-medium">
                Get in touch to discuss how the 3-session radiotherapy program can support you:
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="/book"
                  className="btn-primary px-6 py-3 inline-flex items-center justify-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Request a 10-Minute Call
                </a>
                <a
                  href="/radiotherapy-anxiety"
                  className="btn-secondary px-6 py-3 inline-flex items-center justify-center"
                >
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Learn More
                </a>
              </div>
              
              <p className="text-sm text-gray-500 mt-4">
                Confidential support • Flexible scheduling • Online or in-person sessions available
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Strong CTA Section */}
      <section className="py-20 sm:py-24 bg-white relative fade-in">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-6">
              Ready to Take the Next Step?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed">
              Book your 10-minute call today and find out how hypnotherapy can support you on your journey to healing and transformation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request a 10-Minute Call
              </a>
              <a
                href="/book"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <ArrowRight className="w-5 h-5 mr-2" />
                Book a Session
              </a>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Free consultation</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>No obligation</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Confidential & safe</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-white py-10">
        <p className="max-w-3xl mx-auto px-4 text-center text-sm text-gray-600">
          This website was designed and built by{' '}
          <a href="https://www.roseyco.com/uk/services/web-development" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
            Rosey Co&apos;s web design team
          </a>
          , a marketing agency helping local businesses rank on Google. Read{' '}
          <a href="https://www.roseyco.com/uk/case-studies/sharon-clare-hypnotherapy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
            the Sharon Clare Hypnotherapy case study
          </a>
          .
        </p>
      </section>
    </main>
    </>
  );
};

export default About;
